import React, { useState } from 'react';
import { ApiError } from '../../services/api';
import { marketingService } from '../../services/marketingService';
import { useAuthStore } from '../../stores/authStore';

/**
 * The milestone opt-in ask, rendered inline at the top of the lobby roster.
 *
 * Deliberately a banner and not a modal: a blocking dialog between a returning player and the Begin
 * Adventure button spends goodwill on a marketing ask, and its backdrop would have to either dismiss
 * permanently — harsh, since dismissal is sticky — or do nothing, which is confusing. Two explicit
 * buttons have neither problem, and make every dismissal a deliberate one.
 *
 * There is no checkbox and nothing is pre-checked: pressing Sign me up *is* the consent.
 */
export const MarketingOptInPrompt: React.FC = () => {
  const user = useAuthStore((s) => s.user);
  const patchUser = useAuthStore((s) => s.patchUser);
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [typedEmail, setTypedEmail] = useState('');
  // The address the subscription actually landed on, taken from the server's response rather than
  // re-derived here: the server decides between the address on file and a typed one, and the
  // acknowledgement has to name the address the confirmation link is really going to.
  const [subscribedEmail, setSubscribedEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (!user) return null;
  // `confirmed` outranks the server flag so the banner can stay up to acknowledge the click. Without
  // it, subscribing and declining would look identical — both would simply make the banner vanish,
  // and one of the two is a consent decision the player deserves to see land.
  if (!confirmed && !user.showMarketingPrompt) return null;

  const addressOnFile = user.email;
  const submittable = Boolean(addressOnFile) || typedEmail.trim().length > 0;

  const handleSubscribe = async () => {
    if (!submittable) return;
    setBusy(true);
    setError(null);
    try {
      // Sent only when there is nothing on file; the server ignores it otherwise.
      const { marketingOptIn } = await marketingService.optIn(
        addressOnFile ? undefined : typedEmail.trim(),
      );
      // Patched locally so nothing waits on a refetch; the same fields arrive on the next /auth/me,
      // which is also when the confirmation stops being rendered.
      patchUser({ marketingOptIn, showMarketingPrompt: false });
      setSubscribedEmail(marketingOptIn.email);
      setConfirmed(true);
    } catch (e) {
      // The server's message is the useful one for a rejected address, so prefer it.
      setError(e instanceof ApiError ? e.message : 'Could not sign you up just now. Please try again.');
      setBusy(false);
    }
  };

  const handleDismiss = async () => {
    setBusy(true);
    setError(null);
    // Hidden immediately and unconditionally: a player who said no should not watch a spinner, and
    // should never see the banner again this session even if the write fails.
    patchUser({ showMarketingPrompt: false });
    try {
      await marketingService.dismissPrompt();
    } catch {
      // The refusal is honoured locally regardless; the next /auth/me will re-offer it at worst.
    }
  };

  const buttonStyle: React.CSSProperties = {
    padding: '6px 14px',
    borderRadius: 4,
    border: 'none',
    fontFamily: 'monospace',
    fontSize: 12,
    fontWeight: 'bold',
    cursor: busy ? 'default' : 'pointer',
    opacity: busy ? 0.6 : 1,
  };

  const bannerStyle: React.CSSProperties = {
    background: 'rgba(76, 175, 80, 0.08)',
    border: '1px solid rgba(76, 175, 80, 0.35)',
    borderRadius: 6,
    padding: '10px 14px',
    marginBottom: 18,
  };

  if (confirmed) {
    // Pressing the button is not the last step. Joining the Kit form sends a confirmation link, and
    // an unconfirmed address receives nothing — so "you're on the list" would be true of our
    // database and false of the mailing list, which is the half the player cares about.
    //
    // This is the only place that expectation can be set: the banner is gone by the next visit, so a
    // player who missed the email would have no way to learn one was sent. The address is named
    // because a guest-typed one may hold a typo, and this is the last screen where seeing it back
    // can catch that.
    return (
      <div style={bannerStyle}>
        <div style={{ color: '#e8e8ff', fontSize: 13, marginBottom: 6 }}>
          Almost there — one click to confirm
        </div>
        <div style={{ color: '#999', fontSize: 12, lineHeight: 1.45, overflowWrap: 'anywhere' }}>
          I've sent a confirmation link to <strong style={{ color: '#bbb' }}>{subscribedEmail}</strong>.
          Click it and you're on the list. If it doesn't turn up in a few minutes, check your spam
          folder.
        </div>
      </div>
    );
  }

  return (
    <div className="optin-banner" style={bannerStyle}>
      {/* Full width, above the row — beside the buttons it wrapped while their column sat empty. */}
      <div style={{ color: '#e8e8ff', fontSize: 13, marginBottom: 6 }}>
        Get an email when there's something worth showing
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ color: '#999', fontSize: 12, lineHeight: 1.45 }}>
            Once a month I send a short newsletter about what I'm building — what shipped, what
            broke, what I learned the hard way. Mostly HexCrawl for now. No hype, unsubscribe
            any time.
          </div>
          {addressOnFile ? (
            /* Own line so a long address stands out and cannot wrap mid-domain. `anywhere` breaks
               only when it has to, unlike `break-all`, which breaks eagerly. */
            <div style={{ color: '#bbb', fontSize: 12, marginTop: 6, overflowWrap: 'anywhere' }}>
              Sent to <strong>{addressOnFile}</strong>
            </div>
          ) : (
            /* No address on file. Typing one here subscribes only — it does not become a login, and
               the account stays a guest account. */
            <input
              type="email"
              value={typedEmail}
              onChange={(e) => setTypedEmail(e.target.value)}
              disabled={busy}
              placeholder="you@example.com"
              aria-label="Email address for updates"
              style={{
                marginTop: 8,
                width: '100%',
                boxSizing: 'border-box',
                padding: '6px 8px',
                background: '#222',
                color: '#fff',
                border: '1px solid #444',
                borderRadius: 4,
                fontFamily: 'monospace',
                fontSize: 12,
              }}
            />
          )}
          {error && (
            <div role="alert" style={{ color: '#f88', fontSize: 12, marginTop: 6 }}>{error}</div>
          )}
        </div>
        {/* Row vs column is decided by .optin-actions in App.css, from the banner's own width. */}
        <div className="optin-actions">
          <button
            onClick={handleSubscribe}
            disabled={busy || !submittable}
            style={{
              ...buttonStyle,
              background: submittable ? '#4CAF50' : '#3a5a3c',
              color: submittable ? 'white' : '#9a9a9a',
              cursor: busy || !submittable ? 'default' : 'pointer',
            }}
          >
            Sign me up
          </button>
          <button
            onClick={handleDismiss}
            disabled={busy}
            style={{ ...buttonStyle, background: 'none', border: '1px solid #555', color: '#999' }}
          >
            No thanks
          </button>
        </div>
      </div>
    </div>
  );
};
